import { useState, FormEvent } from "react";
import { categories } from "@/mocks/categories";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/d99leifk7gok24d4ae9g";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("website_alt") as string || "").trim();
    if (honeypot) {
      setStatus("success");
      setStatusMessage("Thank you for subscribing!");
      setEmail("");
      return;
    }

    formData.delete("website_alt");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        setStatusMessage("Thank you for subscribing! Check your inbox.");
        setEmail("");
      } else if (serverMsg.includes("spam") || serverMsg.includes("form data is spam")) {
        setStatus("error");
        setStatusMessage("Something went wrong. Please try again.");
      } else {
        setStatus("error");
        setStatusMessage(serverMsg || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setStatusMessage("Network error. Please try again.");
    }
  };

  const quickLinks = [
    { name: "Hipobuy Spreadsheet Guide", href: "/hipobuy-spreadsheet-guide" },
    { name: "Hipobuy Spreadsheet Review", href: "/hipobuy-spreadsheet-review" },
    { name: "Hipobuy Spreadsheet Safety", href: "/hipobuy-spreadsheet-safety" },
    { name: "Hipobuy vs Superbuy", href: "/hipobuy-vs-superbuy" },
    { name: "Best Resources", href: "/best-hipobuy-resources" },
    { name: "Product Database", href: "/hipobuy-product-database" },
    { name: "All Categories", href: "#categories" },
  ];

  const platformLinks = [
    { name: "Kakobuy Spreadsheet", href: "/kakobuy-spreadsheet-guide" },
    { name: "CSSBuy Spreadsheet", href: "/cssbuy-spreadsheet-guide" },
    { name: "Superbuy Spreadsheet", href: "/superbuy-spreadsheet-guide" },
    { name: "Oopbuy Spreadsheet", href: "/oopbuy-spreadsheet-review" },
  ];

  return (
    <footer className="relative w-full bg-foreground-950 text-background-50">
      <div className="w-full px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">
            <div className="lg:col-span-1">
              <a href="/" className="inline-block mb-4">
                <span className="font-heading text-xl font-bold text-background-50 tracking-tight">
                  hipobuyspread<span className="text-primary-400">.</span>beer
                </span>
              </a>
              <p className="text-sm text-foreground-300 leading-relaxed mb-5">
                The ultimate Hipobuy Spreadsheet resource hub — helping shoppers discover products faster through curated spreadsheet guides and comparisons.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold text-background-50 mb-4 whitespace-nowrap">Quick Links</h4>
              <ul className="space-y-2.5">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-background-50 mb-4 whitespace-nowrap">Platform Comparisons</h4>
              <ul className="space-y-2.5">
                {platformLinks.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-background-50 mb-4 whitespace-nowrap">Newsletter</h4>
              <p className="text-sm text-foreground-300 mb-4 leading-relaxed">
                Get the latest spreadsheet guides and product updates delivered to your inbox.
              </p>
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex flex-col gap-3"
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-honeypot"
                />
                <div className="flex gap-2">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    required
                    className="flex-1 px-4 py-2.5 text-sm bg-foreground-800 border border-foreground-700 rounded-lg text-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                  />
                  <button
                    type="submit"
                    className="px-5 py-2.5 text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Subscribe
                  </button>
                </div>
                {status !== "idle" && (
                  <p className={`text-xs ${status === "success" ? "text-accent-300" : "text-red-400"}`}>
                    {statusMessage}
                  </p>
                )}
              </form>
            </div>
          </div>

          <div className="border-t border-foreground-800 pt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {categories.filter((_, i) => i < 6).map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-xs text-foreground-400 hover:text-background-50 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
              <div className="flex items-center gap-6">
                <p className="text-xs text-foreground-400 whitespace-nowrap">
                  &copy; 2026 hipobuyspread.beer — All rights reserved.
                </p>
                <div className="flex items-center gap-3">
                  <a href="#" className="text-foreground-400 hover:text-background-50 transition-colors duration-150 cursor-pointer">
                    <i className="ri-twitter-x-line"></i>
                  </a>
                  <a href="#" className="text-foreground-400 hover:text-background-50 transition-colors duration-150 cursor-pointer">
                    <i className="ri-reddit-line"></i>
                  </a>
                  <a href="#" className="text-foreground-400 hover:text-background-50 transition-colors duration-150 cursor-pointer">
                    <i className="ri-pinterest-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}